import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import VideoCard from "@/components/feature/VideoCard";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { findVideo, formatViews, relatedVideos } from "@/utils/video";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function DetailPage() {
  const { videoId } = useParams<{ videoId: string }>();
  const video = findVideo(videos, videoId || "");
  const [playing, setPlaying] = useState(false);
  const [copied, setCopied] = useState(false);

  usePageMeta(
    video ? `${video.title} - 黄色免费视频` : "视频未找到 - 黄色免费视频",
    video ? video.description.slice(0, 150) : "该视频暂不可用。",
    video ? `${video.title},免费视频,在线观看,${video.tags.join(",")}` : "免费视频",
  );

  useEffect(() => {
    setPlaying(false);
    setCopied(false);
    window.scrollTo({ top: 0 });
  }, [videoId]);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const category = video ? categories.find((c) => c.slug === video.category) : undefined;

  const related = useMemo(() => (video ? relatedVideos(videos, video) : []), [video]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
    } catch {
      setCopied(true);
    }
  };

  if (!video) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full px-4 md:px-6 py-20 flex flex-col items-center justify-center text-center">
          <span className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950">
            视频不存在或已下线
          </h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的视频资源可能已被移除。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
          >
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <section className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500 flex-wrap">
            <Link to="/" className="hover:text-primary-600 transition-colors">
              首页
            </Link>
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line" />
            </span>
            {category && (
              <>
                <Link
                  to={`/category/${category.slug}`}
                  className="hover:text-primary-600 transition-colors"
                >
                  {category.name}
                </Link>
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line" />
                </span>
              </>
            )}
            <span className="text-foreground-800 font-medium line-clamp-1 max-w-[240px]">
              {video.title}
            </span>
          </nav>

          <h1 className="mt-4 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 leading-snug">
            {video.title} - 黄色免费视频
          </h1>
        </section>

        <section className="w-full px-4 md:px-6 mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          <div className="lg:col-span-2">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-foreground-950">
              <img
                src={video.cover}
                alt={`${video.title} 视频封面`}
                title={`${video.title} - 免费视频`}
                className={`absolute inset-0 w-full h-full object-cover object-top ${playing ? "opacity-40" : ""}`}
              />
              {!playing ? (
                <button
                  type="button"
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-foreground-950/40 hover:bg-foreground-950/50 transition-colors cursor-pointer"
                >
                  <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500 flex items-center justify-center text-background-50">
                    <i className="ri-play-fill text-4xl md:text-5xl leading-none" />
                  </span>
                  <span className="text-sm text-background-100">点击播放（演示）</span>
                </button>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
                  <span className="w-14 h-14 rounded-full bg-primary-500 flex items-center justify-center text-background-50">
                    <i className="ri-loader-4-line text-2xl animate-spin" />
                  </span>
                  <p className="text-background-50 text-sm md:text-base font-medium">
                    演示播放区域：视频源接入中
                  </p>
                  <p className="text-background-200 text-xs max-w-sm">
                    当前为前端播放占位演示，实际视频资源将在后续数据接口接入后播放。
                  </p>
                  <div className="w-full max-w-md h-1 rounded-full bg-background-50/25 overflow-hidden">
                    <div className="h-full w-1/3 bg-primary-500 rounded-full animate-pulse" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setPlaying(false)}
                    className="mt-1 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-background-50/15 text-background-50 text-sm hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-stop-line" />
                    </span>
                    停止演示
                  </button>
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-b border-background-200 pb-5">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line" />
                  </span>
                  {video.duration}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  {video.uploadDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line" />
                  </span>
                  {formatViews(video.views)} 次观看
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-background-100 border border-background-200 text-foreground-800 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={copied ? "ri-check-line" : "ri-share-forward-line"} />
                  </span>
                  {copied ? "链接已复制" : "分享"}
                </button>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line" />
                    </span>
                    返回{category.name}
                  </Link>
                )}
              </div>
            </div>

            <div className="mt-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950 mb-3">
                视频简介
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                {video.description}
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="px-3 py-1.5 text-xs rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors"
                  >
                    {category.name}
                  </Link>
                )}
                {video.tags.map((t) => (
                  <Link
                    key={t}
                    to={`/search?q=${encodeURIComponent(t)}`}
                    className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">
                观看提示
              </h2>
              <ul className="space-y-3 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-accent-600">
                    <i className="ri-information-line" />
                  </span>
                  当前播放器为前端演示区域，暂未接入实际视频源。
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-accent-600">
                    <i className="ri-share-line" />
                  </span>
                  点击“分享”可复制当前页面链接，方便发送给朋友。
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 mt-0.5 flex items-center justify-center text-accent-600">
                    <i className="ri-links-line" />
                  </span>
                  视频信息与封面均来自站点聚合数据，供浏览参考。
                </li>
              </ul>
            </div>
          </aside>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="flex items-end justify-between gap-4 mb-6">
            <div>
              <h2 className="font-heading text-xl md:text-2xl font-extrabold text-foreground-950">
                相关推荐
              </h2>
              <p className="mt-1 text-sm text-foreground-600">根据视频分类为你推荐</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {related.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}